import { MediaKind } from './media-api';

/** The formats a file may be, as its first bytes show them (the rules of `ProductMediaRules.sniff*` of vn). What the browser says about the file does not matter. */
export type SniffedFormat = 'JPEG' | 'PNG' | 'WEBP' | 'HEIC' | 'MP4' | 'MOV' | 'WEBM';

export interface Sniffed {
  format: SniffedFormat;
  type: MediaKind;
  /** The type to declare to the server (`POST /uploads`) and to the cloud (`Content-Type` of the `PUT`): the one the bytes show, never `File.type`. */
  contentType: string;
  /** The browser can draw it in an `<img>` / read it with a `<video>` (HEIC and some MOV it cannot). */
  browserReadable: boolean;
}

/** The number of bytes the sniffing looks at: the same as the server's `HEAD_BYTES`. */
export const HEAD_BYTES = 32;

const HEIC_BRANDS: readonly string[] = ['heic', 'heix', 'heif', 'mif1', 'msf1', 'hevc'];
const HEIF_BRANDS: readonly string[] = ['heif', 'mif1', 'msf1'];
const VIDEO_BRANDS: readonly string[] = ['isom', 'iso2', 'iso3', 'iso4', 'iso5', 'iso6', 'mp41', 'mp42', 'qt  ', 'avc1', 'M4V ', 'hvc1', 'hev1', 'dash'];

function ascii(bytes: Uint8Array, from: number, to: number): string {
  let out = '';
  for (let i = from; i < to && i < bytes.length; i++) {
    out += String.fromCharCode(bytes[i]);
  }
  return out;
}

/** What the first bytes of a file say it is; `null` = none of the allowed formats. */
export function sniffBytes(head: Uint8Array): Sniffed | null {
  const n = head.length;
  if (n >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) {
    return { format: 'JPEG', type: 'PHOTO', contentType: 'image/jpeg', browserReadable: true };
  }
  if (n >= 8 && head[0] === 0x89 && ascii(head, 1, 4) === 'PNG' && head[4] === 0x0d && head[5] === 0x0a && head[6] === 0x1a && head[7] === 0x0a) {
    return { format: 'PNG', type: 'PHOTO', contentType: 'image/png', browserReadable: true };
  }
  if (n >= 12 && ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 12) === 'WEBP') {
    return { format: 'WEBP', type: 'PHOTO', contentType: 'image/webp', browserReadable: true };
  }
  if (n >= 12 && ascii(head, 4, 8) === 'ftyp') {
    const brand = ascii(head, 8, 12);
    if (HEIC_BRANDS.includes(brand)) {
      return { format: 'HEIC', type: 'PHOTO', contentType: HEIF_BRANDS.includes(brand) ? 'image/heif' : 'image/heic', browserReadable: false };
    }
    if (VIDEO_BRANDS.includes(brand)) {
      return brand === 'qt  '
        ? { format: 'MOV', type: 'VIDEO', contentType: 'video/quicktime', browserReadable: true }
        : { format: 'MP4', type: 'VIDEO', contentType: 'video/mp4', browserReadable: true };
    }
  }
  if (n >= 4 && head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) {
    return { format: 'WEBM', type: 'VIDEO', contentType: 'video/webm', browserReadable: true };
  }
  return null;
}

/** Reads the first bytes of a file and sniffs them. */
export async function sniffFile(file: Blob): Promise<Sniffed | null> {
  return sniffBytes(new Uint8Array(await file.slice(0, HEAD_BYTES).arrayBuffer()));
}

/** The `accept` of the file input for the kinds that can still be added (the browser only offers a hint; the bytes decide). */
export function acceptFor(photos: boolean, videos: boolean): string {
  const photo = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif';
  const video = 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm';
  return [photos ? photo : '', videos ? video : ''].filter(Boolean).join(',');
}
